import React from 'react';
import { Box, Typography } from '@mui/material';
import { ServerNotification } from '../../../../types/serverNotifications/serverNotificationTypes';
import useTranslate from '../../../../customHooks/useTranslate';
import NotificationItem from './NotificationItem';
import Button from '../../../../muiComponents/Button';
import { useServerNotifications } from '../../../../contexts/ServerNotificationsContext';
import ActivityEmptyState from '../ActivityEmptyState';
import LoadingPlaceholder from '../../../../muiComponents/LoadingPlaceholder';
import { FeedbackMessages } from '../../../../utils/commonVariables/messages';

/**
 * Displays the notifications section of the activity popover.
 * @param props.notifications The server notifications to display.
 * @returns The notification heading and list.
 */
const Notifications = ({
    notifications,
    onNavigate,
}: {
    notifications: ServerNotification[];
    onNavigate: () => void;
}) => {
    const translate = useTranslate();
    const {
        markAllAsResolved,
        isResolving,
        isLoading,
        hasError,
        getInitialNotifications,
    } = useServerNotifications();
    const unresolvedIds = notifications
        .filter(notification => !notification.resolved)
        .map(notification => notification.id);

    return (
        <Box
            component='section'
            aria-label={translate('notifications')}
            aria-busy={isLoading}
        >
            <Box className='activity__section-title'>
                <Typography component='h2' variant='subtitle1'>
                    {translate('notifications')}
                </Typography>
            </Box>
            {notifications.length > 0 ? (
                <ul className='server-notifications__list'>
                    {notifications.map(notification => (
                        <li key={notification.id}>
                            <NotificationItem
                                notification={notification}
                                onNavigate={onNavigate}
                            />
                        </li>
                    ))}
                </ul>
            ) : isLoading ? (
                <div className='activity__empty' aria-hidden='true'>
                    <LoadingPlaceholder variant='text' height={24} />
                    <LoadingPlaceholder variant='text' height={24} />
                </div>
            ) : !hasError ? (
                <ActivityEmptyState section='notifications' />
            ) : null}
            {hasError && (
                <div className='activity__empty' role='status'>
                    <Typography variant='body2'>
                        {translate(
                            FeedbackMessages.ERROR_LOADING_INITIAL_NOTIFICATIONS
                        )}
                    </Typography>
                    <Button
                        disabled={isLoading}
                        onClick={() => void getInitialNotifications()}
                    >
                        {translate('retry')}
                    </Button>
                </div>
            )}
            <Box component='footer' className='activity__footer'>
                <Button
                    size='small'
                    disabled={isResolving || unresolvedIds.length === 0}
                    onClick={() => void markAllAsResolved(unresolvedIds)}
                >
                    {translate('resolve_all')}
                </Button>
            </Box>
        </Box>
    );
};

export default Notifications;
